import React, { createContext, useContext, useMemo, useState } from 'react';

type DisplaySize = 'extra small' | 'small' | 'medium' | 'large';

interface DisplaySizeContextType {
  size: DisplaySize;
  setDisplaySize: (size: DisplaySize) => void;
}

const DisplaySizeContext = createContext<DisplaySizeContextType | undefined>(undefined);

export const useDisplaySize = () => {
  const context = useContext(DisplaySizeContext);
  if (!context) {
    throw new Error('useDisplaySize must be used within a DisplaySizeProvider');
  }
  return context;
};

interface DisplaySizeProviderProps {
  children: React.ReactNode;
}

export const DisplaySizeProvider: React.FC<DisplaySizeProviderProps> = ({ children }) => {
  const [size, setSize] = useState<DisplaySize>(() => {
    const savedSize = localStorage.getItem('displaySize') as DisplaySize;
    return savedSize || 'medium';
  });

  React.useEffect(() => {
    localStorage.setItem('displaySize', size);
  }, [size]);

  const displaySize = useMemo(
    () => ({
      size,
      setDisplaySize: setSize,
    }),
    [size]
  );

  return (
    <DisplaySizeContext.Provider value={displaySize}>
      {children}
    </DisplaySizeContext.Provider>
  );
}; 